import * as React from "react";
import {
  ColumnFiltersState,
  ColumnVisibilityState,
  RowData,
  SortingState,
  flexRender,
  useTable,
} from "@tanstack/react-table";
import { CustomColumnDef } from "./types/custom-column-def";
import { CustomColumnMeta } from "./types/custom-column-meta";
import {
  AppTable,
  AppTableFeatures,
  appTableFeatures,
} from "./types/table-features";
import { DataTableToolbar } from "./data-table-toolbar";
import { Separator } from "@capacit-com/lara-law-shared-ui";
import { DataTableSkeletonBase } from "./data-table-skeleton-base";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import { DataTablePagination } from "./data-table-pagination";

export type LeveledFiltersConfig = {
  rootColumnId: string; // fx "datasource"
  levels: Record<
    string,
    {
      displayName?: string;
      filters: Record<
        string,
        {
          key: string; // kolonne-id i tabellen
          displayName?: string; // label i UI
          options?: (string | { label: string; value: string })[];
          optionAliases?: Record<string, string[]>;
          type?: "Multi" | "Single";
        }
      >;
    }
  >;
};

interface DataTableProps<TData extends RowData> {
  columns: CustomColumnDef<TData>[];
  data: TData[];
  pageCount: number;
  pageIndex: number;
  pageSize: number;
  sorting?: SortingState;
  search?: string;
  searchTags?: string[];
  columnFilters?: ColumnFiltersState;
  hasToolbar?: boolean;
  filters?: Record<
    string,
    { label: string; value: string; icon?: React.ComponentType }[]
  >;
  setPageIndex: (pageIndex: number) => void;
  setPageSize: (pageSize: number) => void;
  setSorting?: (sorting: SortingState) => void;
  handleSearchChange?: (searchValue: string) => void;
  handleAddSearchTag?: (searchTag: string) => void;
  handleRemoveSearchTag?: (index: number) => void;
  handleClearSearchTags?: () => void;
  setColumnFilters?: (filters: ColumnFiltersState) => void;
  handleRowClick?: (rowIndex: number, event?: React.MouseEvent) => void;
  skeletonComponent?: React.ComponentType<{
    columns: CustomColumnDef<TData>[];
  }>;
  skeletonRows?: number;
  isLoading?: boolean;
  stickyHeader?: boolean;
  pageSizeOptions?: number[];
  alwaysShowFilters?: boolean;
  shouldDisplayHoverContent?: boolean;
  createNode?: React.ReactNode;
  leveledFilters?: LeveledFiltersConfig;
  onRootSelectionChange?: () => void;
  showSearchSyntaxHelp?: boolean;
  /** Applies the predefined filter selection. Given only by tables that have
   *  one, which is what makes the toolbar offer the action at all. */
  onApplyDefaultFilters?: () => void;
  /** Rendered next to the search box, for actions on the search itself. */
  searchActions?: React.ReactNode;
  defaultColumnVisibility?: ColumnVisibilityState;
  /** Scroll the rows INSIDE the table region (bounded-height layouts): the
   *  toolbar and pagination stay pinned while only the rows scroll. Pair with
   *  stickyHeader. Off by default — pages that scroll as a whole are unchanged. */
  scrollRows?: boolean;
  /** Rendered between the toolbar and the rows, e.g. a heading that says what the rows are. */
  beforeTable?: React.ReactNode;
  /** Replaces the pagination footer, for tables that show a capped preview instead of pages. */
  footer?: React.ReactNode;
  /** Drops the pagination footer altogether (a preview whose rows all fit). */
  hidePagination?: boolean;
  /** Keeps the toolbar (and `beforeTable`) but renders neither rows nor pagination — for a
   *  page whose search has switched to showing something other than these rows. */
  hideRows?: boolean;
  /** Extra classes on the root, e.g. `h-auto` when the table should size to its rows. */
  className?: string;
}

export function DataTable<TData extends RowData>({
  columns,
  data,
  search,
  searchTags,
  pageCount,
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  filters,
  hasToolbar = true,
  setPageIndex,
  setPageSize,
  setSorting,
  handleSearchChange,
  handleAddSearchTag,
  handleRemoveSearchTag,
  handleClearSearchTags,
  setColumnFilters,
  handleRowClick,
  skeletonComponent,
  skeletonRows = 25,
  stickyHeader = false,
  isLoading = false,
  pageSizeOptions,
  alwaysShowFilters = false,
  shouldDisplayHoverContent = false,
  createNode,
  leveledFilters,
  onRootSelectionChange,
  showSearchSyntaxHelp = false,
  onApplyDefaultFilters,
  searchActions,
  defaultColumnVisibility,
  scrollRows = false,
  beforeTable,
  footer,
  hideRows = false,
  hidePagination = false,
  className,
}: DataTableProps<TData>) {
  const [columnVisibility, setColumnVisibility] =
    React.useState<ColumnVisibilityState>(() => defaultColumnVisibility ?? {});
  React.useEffect(() => {
    if (!defaultColumnVisibility) return;
    setColumnVisibility((prev) => {
      const next = { ...prev, ...defaultColumnVisibility };
      const unchanged = Object.entries(defaultColumnVisibility).every(
        ([key, value]) => prev[key] === value
      );
      return unchanged ? prev : next;
    });
  }, [defaultColumnVisibility]);
  const [rowSelection, setRowSelection] = React.useState({});
  const [hoveredRowId, setHoveredRowId] = React.useState<string | null>(null);

  // The skeleton is built from column defs, not from the table, so drop the
  // columns the table itself hides (filter-only or datasource-dependent ones).
  // Otherwise the placeholder shows extra headers and the layout jumps when
  // the data lands.
  const visibleColumns = React.useMemo(
    () =>
      columns.filter((column) => {
        const id =
          column.id ??
          ("accessorKey" in column && typeof column.accessorKey === "string"
            ? column.accessorKey
            : undefined);
        return id === undefined || columnVisibility[id] !== false;
      }),
    [columns, columnVisibility]
  );

  const table: AppTable<TData> = useTable<AppTableFeatures, TData>({
    features: appTableFeatures,
    data,
    columns,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === "function"
          ? updater({ pageIndex, pageSize })
          : updater;
      setPageIndex(newPagination.pageIndex);
      setPageSize(newPagination.pageSize);
    },
    onSortingChange: (updater) => {
      if (setSorting) {
        setSorting(
          typeof updater === "function" ? updater(sorting ?? []) : updater
        );
      }
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: (updater) => {
      if (setColumnFilters) {
        const next =
          typeof updater === "function"
            ? updater(columnFilters ?? [])
            : updater;
        setColumnFilters(next);
      }
    },
    onRowSelectionChange: setRowSelection,
  });

  return (
    <div
      className={cn("flex flex-col h-full w-full space-y-4 pb-4", className)}
    >
      {hasToolbar && (
        <>
          <DataTableToolbar
            table={table}
            search={search}
            searchTags={searchTags}
            handleAddSearchTag={handleAddSearchTag}
            handleRemoveSearchTag={handleRemoveSearchTag}
            handleClearSearchTags={handleClearSearchTags}
            handleSearchChange={handleSearchChange}
            filters={filters}
            alwaysShowFilters={alwaysShowFilters}
            createNode={createNode}
            leveledFilters={leveledFilters}
            onRootSelectionChange={onRootSelectionChange}
            showSearchSyntaxHelp={showSearchSyntaxHelp}
            onApplyDefaultFilters={onApplyDefaultFilters}
            searchActions={searchActions}
          />
          <Separator />
        </>
      )}

      {beforeTable}

      {hideRows ? null : (
        <div className="flex flex-col grow overflow-hidden">
          {/* scrollRows: the shared Table's own wrapper (data-slot=table-container,
            overflow-x-auto) is the scroll container sticky headers stick to, so
            the vertical scroll must live on IT — bounded by this flex column.
            @container/table: lets columns react to the width of the table
            itself (`@max-[…]/table:` variants), e.g. let a long text column
            wrap only once the table gets narrow, instead of to the viewport. */}
          <div
            className={cn(
              "grow flex flex-col min-h-0 rounded-md @container/table",
              scrollRows &&
                "[&_[data-slot=table-container]]:grow [&_[data-slot=table-container]]:min-h-0 [&_[data-slot=table-container]]:overflow-y-auto"
            )}
          >
            {isLoading && skeletonComponent ? (
              <DataTableSkeletonBase
                columns={visibleColumns}
                SkeletonRow={skeletonComponent}
                rowCount={skeletonRows}
              />
            ) : table.getRowModel().rows.length === 0 ? (
              <div className="grow flex items-center justify-center">
                Ingen resultater
              </div>
            ) : (
              <Table className="table-auto">
                <TableHeader
                  className={cn(stickyHeader && "sticky top-0 z-10")}
                >
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableHead
                            key={header.id}
                            className={
                              (header.column.columnDef.meta as CustomColumnMeta)
                                ?.headerClassName
                            }
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <React.Fragment key={row.id}>
                      {shouldDisplayHoverContent ? (
                        <TableRow
                          data-state={row.getIsSelected() && "valgte"}
                          className={cn(
                            handleRowClick && "cursor-pointer",
                            "group"
                          )}
                          onClick={(event) => {
                            if (handleRowClick) {
                              handleRowClick(row.index, event);
                            }
                          }}
                          onMouseEnter={() => {
                            setHoveredRowId(row.id);
                          }}
                          onMouseLeave={() => {
                            setHoveredRowId(null);
                          }}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell
                              key={cell.id}
                              className={
                                (cell.column.columnDef.meta as CustomColumnMeta)
                                  ?.cellClassName
                              }
                            >
                              <div className="min-h-12 flex items-center justify-between">
                                {flexRender(cell.column.columnDef.cell, {
                                  ...cell.getContext(),
                                  isHovering: hoveredRowId === row.id,
                                })}
                              </div>
                            </TableCell>
                          ))}
                        </TableRow>
                      ) : (
                        <TableRow
                          data-state={row.getIsSelected() && "valgte"}
                          className={cn(
                            handleRowClick && "cursor-pointer",
                            "group"
                          )}
                          onClick={(event) => {
                            if (handleRowClick) {
                              handleRowClick(row.index, event);
                            }
                          }}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell
                              key={cell.id}
                              className={
                                (cell.column.columnDef.meta as CustomColumnMeta)
                                  ?.cellClassName
                              }
                            >
                              <div className="min-h-12 flex items-center justify-between">
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </div>
                            </TableCell>
                          ))}
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      )}
      {hideRows || hidePagination
        ? null
        : (footer ?? (
            <DataTablePagination
              pageCount={pageCount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              setPageIndex={setPageIndex}
              setPageSize={setPageSize}
              pageSizeOptions={pageSizeOptions}
            />
          ))}
    </div>
  );
}
